import { useMemo } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import EntityImageDialog, { type EntityImageDialogCopy, type EntityImageDialogProps } from "../../EntityImage/EntityImageDialog";

export type { EntityImageDialogSurface as PortraitDialogSurface } from "../../EntityImage/EntityImageDialog";

type CharacterPortraitDialogProps = Omit<
  EntityImageDialogProps,
  "entityName" | "entityRoleLabel" | "entityImageUrl" | "entityImagePrompt" | "fallbackIconSrc" | "imageNoun" | "copy" | "analytics" | "imagesLoading" | "onAddImage"
> & {
  characterId: string;
  characterName: string;
  characterRoleLabel?: string | null;
  characterImageUrl?: string | null;
  characterImagePrompt?: string | null;
  portraitsLoading: boolean;
  onAddPortrait: () => void;
};

const COPY: EntityImageDialogCopy = {
  descriptionLabel: (usingFreeformStyle) => (usingFreeformStyle ? "Appearance & style" : "Appearance details"),
  // Kept to one line so the composer stays at thumbnail height; the summarizer already reads their Story Bible traits.
  descriptionPlaceholder: (usingFreeformStyle) =>
    usingFreeformStyle ? "We'll use their Story Bible traits. Add an art style or anything else" : "We'll use their Story Bible traits. Add outfit, mood, or a setting",
  emptyResultsText: "Your portraits will appear here",
  emptyResultsImageSrc: "/assets/portrait-group.webp",
  emptyResultsImageClassName: "w-[240px] max-w-full",
  stylesPopoverWidth: "420px",
};

export default function CharacterPortraitDialog({
  characterId,
  characterName,
  characterRoleLabel,
  characterImageUrl,
  characterImagePrompt,
  portraitsLoading,
  onAddPortrait,
  ...rest
}: CharacterPortraitDialogProps) {
  const analytics = useMemo(
    () => ({
      uploadFilesAdded: (props: { addedCount: number; totalPendingCount: number; rejectedDueToLimit: boolean }) =>
        void AppEventPublisher.characterImage.uploadFilesAdded({ characterId, ...props }),
      uploadSaveClicked: (props: { fileCount: number }) => void AppEventPublisher.characterImage.uploadSaveClicked({ characterId, ...props }),
    }),
    [characterId],
  );

  return (
    <EntityImageDialog
      {...rest}
      entityName={characterName}
      entityRoleLabel={characterRoleLabel}
      entityImageUrl={characterImageUrl}
      entityImagePrompt={characterImagePrompt}
      fallbackIconSrc="/assets/character-icon.svg"
      imageNoun="portrait"
      copy={COPY}
      analytics={analytics}
      imagesLoading={portraitsLoading}
      onAddImage={onAddPortrait}
    />
  );
}
